import React, { useState, useMemo } from 'react'

export function ArchitectureDecisions({ data }) {
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')

  const filteredADRs = useMemo(() => {
    return data.adrs
      .filter(adr => filter === 'all' || adr.status === filter)
      .filter(adr => adr.title.toLowerCase().includes(search.toLowerCase()) ||
        adr.description.toLowerCase().includes(search.toLowerCase()) ||
        adr.tags.some(t => t.toLowerCase().includes(search.toLowerCase())))
      .sort((a, b) => new Date(b.date) - new Date(a.date))
  }, [data.adrs, filter, search])

  const statusCounts = useMemo(() => {
    const counts = { accepted: 0, proposed: 0, deprecated: 0, superseded: 0 }
    data.adrs.forEach(adr => counts[adr.status]++)
    return counts
  }, [data.adrs])

  return (
    <div className="architecture-decisions">
      <div className="adr-header">
        <h2>Architecture Decision Records</h2>
        <p className="adr-subtitle">Key technical decisions with rationale and consequences</p>
      </div>

      <div className="adr-filters">
        <div className="search-box">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            placeholder="Search ADRs..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search ADRs"
          />
        </div>
        <div className="status-filter" role="group" aria-label="Filter by status">
          <button
            className={`status-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All <span className="count">{data.adrs.length}</span>
          </button>
          <button
            className={`status-btn ${filter === 'accepted' ? 'active' : ''}`}
            onClick={() => setFilter('accepted')}
            style={{ '--status-color': 'var(--status-good)' }}
          >
            Accepted <span className="count">{statusCounts.accepted}</span>
          </button>
          <button
            className={`status-btn ${filter === 'proposed' ? 'active' : ''}`}
            onClick={() => setFilter('proposed')}
            style={{ '--status-color': 'var(--status-warning)' }}
          >
            Proposed <span className="count">{statusCounts.proposed}</span>
          </button>
          <button
            className={`status-btn ${filter === 'deprecated' ? 'active' : ''}`}
            onClick={() => setFilter('deprecated')}
            style={{ '--status-color': 'var(--text-muted)' }}
          >
            Deprecated <span className="count">{statusCounts.deprecated}</span>
          </button>
          <button
            className={`status-btn ${filter === 'superseded' ? 'active' : ''}`}
            onClick={() => setFilter('superseded')}
            style={{ '--status-color': 'var(--series-5)' }}
          >
            Superseded <span className="count">{statusCounts.superseded}</span>
          </button>
        </div>
      </div>

      <div className="adr-list">
        {filteredADRs.map(adr => (
          <article key={adr.id} className="adr-card">
            <header className="adr-card-header">
              <div className="adr-meta">
                <span className={`adr-status ${adr.status}`}>{adr.status}</span>
                <span className="adr-number">ADR-{adr.id.toString().padStart(4, '0')}</span>
                <time className="adr-date" dateTime={adr.date}>{new Date(adr.date).toLocaleDateString()}</time>
              </div>
              <div className="adr-tags">
                {adr.tags.map(tag => <span key={tag} className="adr-tag">{tag}</span>)}
              </div>
            </header>
            <div className="adr-content">
              <h3 className="adr-title">{adr.title}</h3>
              <p className="adr-description">{adr.description}</p>
              <div className="adr-sections">
                {adr.context && (
                  <div className="adr-section">
                    <h4>Context</h4>
                    <p>{adr.context}</p>
                  </div>
                )}
                {adr.decision && (
                  <div className="adr-section">
                    <h4>Decision</h4>
                    <p>{adr.decision}</p>
                  </div>
                )}
                {adr.consequences && (
                  <div className="adr-section">
                    <h4>Consequences</h4>
                    <ul>
                      {adr.consequences.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </div>
            <footer className="adr-footer">
              <span className="adr-author">By: {adr.author}</span>
              {adr.links && adr.links.map((link, i) => (
                <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="adr-link">
                  {link.label}
                </a>
              ))}
            </footer>
          </article>
        ))}

        {filteredADRs.length === 0 && (
          <div className="empty-state">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <p>No ADRs match the current filters</p>
          </div>
        )}
      </div>

      <style jsx>{`
        .architecture-decisions {
          display: flex;
          flex-direction: column;
          gap: 16px;
          height: 100%;
          overflow: auto;
        }
        .adr-header h2 {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 4px;
        }
        .adr-subtitle {
          font-size: 13px;
          color: var(--text-secondary);
        }
        .adr-filters {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .search-box {
          position: relative;
          flex: 1;
          min-width: 200px;
          max-width: 400px;
        }
        .search-box svg {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-muted);
          pointer-events: none;
        }
        .search-box input {
          width: 100%;
          padding: 8px 12px 8px 40px;
          border: 1px solid var(--border);
          border-radius: 6px;
          background: var(--surface-1);
          color: var(--text-primary);
          font-size: 13px;
        }
        .search-box input:focus {
          outline: none;
          border-color: var(--series-1);
          box-shadow: 0 0 0 2px var(--sequential-100);
        }
        .status-filter {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .status-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 12px;
          border-radius: 999px;
          font-size: 12px;
          font-weight: 500;
          color: var(--text-secondary);
          background: var(--page-plane);
          border: 1px solid var(--border);
        }
        .status-btn:hover {
          color: var(--text-primary);
          background: var(--surface-1);
        }
        .status-btn.active {
          color: var(--status-color);
          border-color: var(--status-color);
          background: var(--page-plane);
        }
        .count {
          background: var(--border);
          padding: 0 6px;
          border-radius: 999px;
          font-size: 11px;
        }
        .status-btn.active .count {
          background: var(--status-color);
          color: white;
        }
        .adr-list {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 16px;
          overflow: auto;
        }
        .adr-card {
          background: var(--surface-1);
          border: 1px solid var(--border);
          border-radius: 8px;
          overflow: hidden;
          transition: box-shadow 0.2s;
        }
        .adr-card:hover {
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        }
        .adr-card-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          padding: 12px 16px;
          border-bottom: 1px solid var(--border);
          background: var(--page-plane);
          flex-wrap: wrap;
          gap: 8px;
        }
        .adr-meta {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .adr-status {
          display: inline-flex;
          align-items: center;
          padding: 2px 8px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
        }
        .adr-status.accepted { background: #dcfce7; color: #166534; }
        .adr-status.proposed { background: #fef3c7; color: #92400e; }
        .adr-status.deprecated { background: var(--border); color: var(--text-muted); }
        .adr-status.superseded { background: #fce7f3; color: #9d174d; }
        .adr-number {
          font-family: ui-monospace, SFMono-Regular, monospace;
          font-size: 12px;
          color: var(--text-muted);
        }
        .adr-date {
          font-size: 12px;
          color: var(--text-muted);
        }
        .adr-tags {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }
        .adr-tag {
          padding: 2px 8px;
          border-radius: 999px;
          font-size: 11px;
          background: var(--sequential-100);
          color: var(--sequential-550);
        }
        @media (prefers-color-scheme: dark) {
          :root:where(:not([data-theme="light"])) .adr-tag,
          [data-theme="dark"] .adr-tag {
            background: var(--sequential-700);
            color: var(--sequential-250);
          }
        }
        .adr-content {
          padding: 16px;
        }
        .adr-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 8px;
        }
        .adr-description {
          font-size: 13px;
          color: var(--text-secondary);
          margin: 0 0 16px;
          line-height: 1.6;
        }
        .adr-sections {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .adr-section {
          padding: 12px;
          background: var(--page-plane);
          border-radius: 6px;
          border-left: 3px solid var(--series-1);
        }
        .adr-section h4 {
          font-size: 12px;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0 0 8px;
        }
        .adr-section p {
          font-size: 13px;
          color: var(--text-primary);
          margin: 0;
          line-height: 1.6;
        }
        .adr-section ul {
          margin: 0;
          padding-left: 20px;
        }
        .adr-section li {
          font-size: 13px;
          color: var(--text-primary);
          margin: 4px 0;
          line-height: 1.5;
        }
        .adr-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          border-top: 1px solid var(--border);
          background: var(--page-plane);
          flex-wrap: wrap;
          gap: 8px;
        }
        .adr-author {
          font-size: 12px;
          color: var(--text-muted);
        }
        .adr-link {
          font-size: 12px;
          color: var(--series-1);
          text-decoration: none;
        }
        .adr-link:hover {
          text-decoration: underline;
        }
        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: var(--text-muted);
          gap: 12px;
        }
      `}</style>
    </div>
  )
}